/**
 * Admin Audit — recent admin actions feed.
 * Routes: /admin/audit
 *
 * Shows the last 100 admin actions (hide/restore/suspend/cleanup/etc.) with
 * filtering by action family and admin. Expandable rows reveal the raw
 * payload JSON. Pagination-ready (currently single-page, limit configurable).
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, RefreshCw, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_FAMILIES = [
  { id: '',           label: 'All' },
  { id: 'content.',   label: 'Content' },
  { id: 'user.',      label: 'Users' },
  { id: 'cleanup.',   label: 'Cleanup' },
  { id: 'report.',    label: 'Reports' },
];

const ACTION_LABEL = {
  'content.hidden':            { text: 'Hide content',     tone: 'red'   },
  'content.restored':          { text: 'Restore content',  tone: 'green' },
  'user.suspended':            { text: 'Suspend user',     tone: 'red'   },
  'user.unsuspended':          { text: 'Unsuspend user',   tone: 'green' },
  'report.dismissed':          { text: 'Dismiss report',   tone: 'gray'  },
  'cleanup.approve_old_pending':  { text: 'Approve pending', tone: 'gold' },
  'cleanup.remove_broken_images': { text: 'Hide broken imgs', tone: 'gold' },
  'cleanup.rebuild_stats':        { text: 'Rebuild stats', tone: 'gold' },
};

const TONE = {
  red:   'bg-red-500/10 text-red-400 border-red-500/20',
  green: 'bg-green-500/10 text-green-400 border-green-500/20',
  gold:  'bg-[#c8b273]/10 text-[#c8b273] border-[#c8b273]/30',
  gray:  'bg-[#1f221f] text-[#a8b0a8] border-[#2a2e2a]',
};

export default function AdminAudit() {
  const navigate = useNavigate();
  const token = localStorage.getItem('carpcast_token');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [family, setFamily] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [expanded, setExpanded] = useState(null);
  const [limit, setLimit] = useState(100);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (family) params.set('action', family);
      params.set('limit', String(limit));
      const r = await axios.get(`${API}/admin/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data.rows || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admins only');
        navigate('/dashboard', { replace: true });
        return;
      }
      toast.error('Could not load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [family, limit]);

  const admins = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.admin_username && s.add(r.admin_username));
    return Array.from(s).sort();
  }, [rows]);

  const visible = useMemo(() => {
    let v = rows;
    if (adminFilter) v = v.filter((r) => r.admin_username === adminFilter);
    const sorted = [...v].sort((a, b) => {
      if (sortBy === 'action') return (a.action || '').localeCompare(b.action || '');
      if (sortBy === 'admin')  return (a.admin_username || '').localeCompare(b.admin_username || '');
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sortBy === 'created_at_asc' ? ta - tb : tb - ta;
    });
    return sorted;
  }, [rows, adminFilter, sortBy]);

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12" data-testid="admin-audit">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#c8b273] mb-1">
              <Shield className="inline-block w-3 h-3 mr-1.5 -mt-0.5" />
              Admin · Audit log
            </p>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              Recent admin actions
            </h1>
          </div>
          <button
            onClick={load}
            className="text-[#8a948a] hover:text-[#c8b273] p-2"
            data-testid="audit-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4 border-b border-[#1f221f] pb-3">
          {ACTION_FAMILIES.map((f) => (
            <button
              key={f.id || 'all'}
              onClick={() => setFamily(f.id)}
              className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
                family === f.id
                  ? 'bg-[#c8b273] text-black'
                  : 'bg-[#1f221f] text-[#a8b0a8] hover:text-white'
              }`}
              data-testid={`audit-family-${f.id || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <select
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            className="bg-[#0b0d0b] border border-[#1f221f] rounded px-2 py-1.5 text-white"
            data-testid="audit-admin-select"
          >
            <option value="">All admins</option>
            {admins.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#0b0d0b] border border-[#1f221f] rounded px-2 py-1.5 text-white"
            data-testid="audit-sort-select"
          >
            <option value="created_at_desc">Newest first</option>
            <option value="created_at_asc">Oldest first</option>
            <option value="action">Action A→Z</option>
            <option value="admin">Admin A→Z</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-[#0b0d0b] border border-[#1f221f] rounded px-2 py-1.5 text-white"
            data-testid="audit-limit-select"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>

        {loading && !rows.length ? (
          <div className="glass-card p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#c8b273] animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="audit-empty">
            <Shield className="w-10 h-10 text-[#4a504a] mx-auto mb-3" />
            <p className="text-white font-semibold">No audit entries yet</p>
            <p className="text-[#8a948a] text-sm mt-1">Actions taken across moderation, suspensions, and cleanup will appear here.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="audit-list">
            {visible.map((r) => {
              const meta = ACTION_LABEL[r.action] || { text: r.action, tone: 'gray' };
              const isOpen = expanded === r.id;
              return (
                <div key={r.id} className="glass-card p-3" data-testid={`audit-row-${r.id}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="w-full text-left flex items-center justify-between gap-3"
                    data-testid={`audit-row-toggle-${r.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${TONE[meta.tone]}`}>
                        {meta.text}
                      </span>
                      <span className="text-sm text-white truncate">
                        <span className="text-[#c8b273] font-mono">{r.admin_username || r.admin_id.slice(0, 8)}</span>
                        <span className="text-[#8a948a]"> · {new Date(r.created_at).toLocaleString()}</span>
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#8a948a] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <pre className="mt-3 text-[11px] font-mono text-[#c0c4c0] bg-[#050605] border border-[#1f221f] rounded p-3 overflow-x-auto" data-testid={`audit-payload-${r.id}`}>
{JSON.stringify(r.payload || {}, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => setLimit((n) => Math.min(n + 100, 500))}
            variant="outline"
            disabled={rows.length < limit}
            data-testid="audit-load-more"
          >
            Load more
          </Button>
        </div>
      </div>
    </div>
  );
}
